import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { eventsApi } from '@/features/events/api';
import { eventPhase, type EventPhase } from '@/features/project/eventPhase';
import { useFormPanel } from '@/components/layout/formPanelStore';
import { useSelectedBrandId } from '@/lib/branding/brandContextStore';
import { queryKeys } from '@/lib/api/queryKeys';
import { Breadcrumbs } from '@/components/ui/Breadcrumbs';
import { usePrefsStore } from '@/lib/prefs/usePrefsStore';
import { useT } from '@/lib/i18n/useT';
import { cn } from '@/lib/cn';
import type { XkEvent } from '@/lib/api/types';

/**
 * Cuántos eventos se listan en una celda del mes antes de resumir.
 *
 * Dos, no tres: desde que cada uno lleva su proyecto debajo ocupa el doble de
 * alto, y tres apilados desbordaban la celda. El resto se resume con «+N más»,
 * que lleva a la semana — donde caben todos sin recortar.
 */
const MAX_POR_DIA = 2;

/** El color de cada fase. El mismo criterio que la pestaña Seguir. */
const COLOR: Record<EventPhase, string> = {
  live: 'var(--color-success)',
  upcoming: 'var(--color-xk-accent)',
  edit: 'var(--color-warn)',
  done: 'var(--color-xk-text-muted)',
};

/**
 * El calendario de la marca.
 *
 * Responde la pregunta que ninguna otra pantalla contesta: **qué hay este mes y
 * qué se solapa**. La lista de proyectos ordena por proyecto y la de eventos por
 * fecha, pero ninguna enseña dos ferias el mismo sábado — y ése es justo el
 * problema que hay que ver antes de que llegue el sábado.
 *
 * ## Un evento ocupa TODOS sus días
 *
 * Es la diferencia entre un calendario y una lista con casillas. Una feria del
 * 10 al 12 sale los tres días; pintarla sólo el 10 —que es lo que hacía el
 * esbozo anterior— deja el 11 y el 12 en blanco y hace creer que están libres.
 *
 * ## La semana empieza en lunes
 *
 * Y los nombres de los días salen de `Intl` con el idioma de la herramienta. Un
 * calendario en el que el fin de semana no cae al final se lee mal, y el fin de
 * semana es cuando ocurren los eventos.
 */
export function CalendarPage({
  /**
   * Incrustado dentro de otra pantalla (el perfil), sin marco de página.
   *
   * Es el MISMO calendario, no una versión reducida: había un segundo esbozo
   * viviendo sólo en el perfil, con su propio mes fijo y sus propios fallos, y
   * dos calendarios que se contradicen son peor que uno.
   */
  embedded,
}: { embedded?: boolean } = {}) {
  const t = useT();
  const language = usePrefsStore((s) => s.language);
  const brandId = useSelectedBrandId();
  const openForm = useFormPanel((s) => s.openForm);

  /*
   * Mes o semana.
   *
   * El mes contesta «¿qué hay y qué se solapa?»; la semana contesta «¿qué me
   * toca?». Son dos preguntas distintas y por eso son dos vistas, no un zoom:
   * en el mes lo que importa es la forma —dónde se amontona—, y en la semana,
   * quién ocupa cada día.
   */
  const [vista, setVista] = useState<'month' | 'week'>('month');

  // El mes que se está mirando, siempre el día 1 para no arrastrar desfases al
  // saltar de un mes de 31 días a uno de 30.
  const [mes, setMes] = useState(() => {
    const h = new Date();
    return new Date(h.getFullYear(), h.getMonth(), 1);
  });
  // El lunes de la semana que se está mirando.
  const [semana, setSemana] = useState(() => lunesDe(new Date()));

  const eventosQ = useQuery({
    queryKey: queryKeys.events.byBrand(brandId ?? 0),
    queryFn: () => eventsApi.listEvents({ companyId: brandId }),
    enabled: brandId != null,
  });
  const proyectosQ = useQuery({
    queryKey: queryKeys.eventGroups.byBrand(brandId ?? 0),
    queryFn: () => eventsApi.listGroups({ companyId: brandId }),
    enabled: brandId != null,
  });

  const nombreProyecto = useMemo(
    () => new Map((proyectosQ.data?.items ?? []).map((p) => [p.id, p.name])),
    [proyectosQ.data],
  );

  const eventos = eventosQ.data?.items ?? [];

  /**
   * Las celdas del mes, con sus eventos.
   *
   * La rejilla se rellena de lunes a domingo y arrastra los días de los meses
   * vecinos en gris: un mes que empieza en jueves con tres huecos vacíos
   * delante se lee peor que uno que enseña el fin de semana anterior.
   */
  const celdas = useMemo(() => {
    const primero = new Date(mes.getFullYear(), mes.getMonth(), 1);
    const ultimo = new Date(mes.getFullYear(), mes.getMonth() + 1, 0);
    // `getDay()` da 0 para domingo; con la semana empezando en lunes, domingo
    // es el sexto hueco, no el primero.
    const desplazamiento = (primero.getDay() + 6) % 7;
    const inicio = new Date(primero);
    inicio.setDate(primero.getDate() - desplazamiento);
    const total = Math.ceil((desplazamiento + ultimo.getDate()) / 7) * 7;

    return Array.from({ length: total }, (_, i) => {
      const dia = new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate() + i);
      return {
        dia,
        deOtroMes: dia.getMonth() !== mes.getMonth(),
        eventos: eventos.filter((e) => ocupa(e, dia)),
      };
    });
  }, [mes, eventos]);

  const hoy = new Date();
  const esHoy = (d: Date) =>
    d.getFullYear() === hoy.getFullYear() &&
    d.getMonth() === hoy.getMonth() &&
    d.getDate() === hoy.getDate();

  // Cuántos eventos tocan este mes. Es la cifra que se busca al abrir.
  const delMes = eventos.filter((e) =>
    celdas.some((c) => !c.deOtroMes && c.eventos.includes(e)),
  ).length;

  /**
   * Abrir el resumen en el panel derecho, sin salir del calendario.
   *
   * Mirar la agenda es un barrido: se abren cinco eventos seguidos para ver
   * cuál es cuál. Si cada uno te saca a otra pantalla hay que volver cinco
   * veces, y cada vuelta pierde el mes que estabas mirando.
   */
  const abrirResumen = (e: XkEvent) =>
    openForm({
      kind: 'event-summary',
      mode: 'edit',
      title: e.name ?? t('calendar.summary.title'),
      context: { eventId: e.id },
    });

  const mover = (paso: number) => {
    if (vista === 'week') {
      setSemana(new Date(semana.getFullYear(), semana.getMonth(), semana.getDate() + 7 * paso));
      return;
    }
    setMes(new Date(mes.getFullYear(), mes.getMonth() + paso, 1));
  };

  /**
   * El rótulo de la semana: «11 – 17 de agosto de 2026», y con el mes de los
   * dos extremos cuando la semana los cruza. Repetir el mes en las dos puntas
   * cuando es el mismo es ruido.
   */
  const finDeSemana = new Date(semana.getFullYear(), semana.getMonth(), semana.getDate() + 6);
  const titulo =
    vista === 'week'
      ? `${semana.toLocaleDateString(language, {
          day: 'numeric',
          ...(semana.getMonth() === finDeSemana.getMonth() ? {} : { month: 'short' }),
        })} – ${finDeSemana.toLocaleDateString(language, {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })}`
      : mes.toLocaleDateString(language, { month: 'long', year: 'numeric' });

  const delaSemana = eventos.filter((e) =>
    Array.from({ length: 7 }, (_, i) =>
      new Date(semana.getFullYear(), semana.getMonth(), semana.getDate() + i),
    ).some((d) => ocupa(e, d)),
  ).length;

  const crearEse = (dia: Date) =>
    openForm({
      kind: 'event',
      mode: 'create',
      title: t('calendar.newOnDay', { day: dia.toLocaleDateString(language, { day: 'numeric', month: 'long' }) }),
      // La fecha viaja al formulario: quien pulsa el 14 ya dijo cuándo, y
      // volver a pedírsela es hacerle repetir lo que acaba de decir.
      context: { brandId, initialDate: fechaLocal(dia) },
    });

  if (brandId == null) {
    return (
      <div className={embedded ? '' : 'page'}>
        <p className="text-sm text-[var(--color-xk-text-muted)]">{t('calendar.noBrand')}</p>
      </div>
    );
  }

  return (
    <div className={embedded ? 'rounded-2xl border border-[var(--color-xk-border)] p-4' : 'page'}>
      {embedded ? null : (
        <Breadcrumbs className="mb-4" items={[{ label: t('sidebar.calendar') }]} />
      )}

      <header className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1">
          {/* Las flechas mueven lo que se está viendo: un mes o una semana.
              Que en la vista de semana saltaran de mes en mes obligaría a
              contar semanas para llegar al viernes que viene. */}
          <BotonMes onClick={() => mover(-1)} label={t('calendar.prev')}>
            <ChevronLeft className="h-4 w-4" />
          </BotonMes>
          <BotonMes onClick={() => mover(1)} label={t('calendar.next')}>
            <ChevronRight className="h-4 w-4" />
          </BotonMes>
        </div>

        {/* En el perfil ya hay un h1: aquí baja a h2 para no partir el
            esquema de encabezados de la página que lo aloja. */}
        {embedded ? (
          <h2 className="text-base font-extrabold first-letter:uppercase text-[var(--color-fg-primary)]">
            {titulo}
          </h2>
        ) : (
          <h1 className="text-lg font-extrabold first-letter:uppercase text-[var(--color-fg-primary)]">
            {titulo}
          </h1>
        )}

        {/* Mes o semana. Dos botones y no un desplegable: son dos, se alternan
            a menudo, y un desplegable esconde la mitad de la elección. */}
        <div className="inline-flex overflow-hidden rounded-lg border border-[var(--color-xk-border)] text-[11px] font-bold">
          {(['month', 'week'] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setVista(v)}
              aria-pressed={vista === v}
              className={cn(
                'px-2.5 py-1 transition-colors',
                vista === v
                  ? 'bg-[var(--color-xk-accent)] text-[var(--color-fg-on-accent)]'
                  : 'text-[var(--color-xk-text-muted)] hover:text-[var(--color-xk-lavender)]',
              )}
            >
              {t(`calendar.view.${v}`)}
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={() => {
            setMes(new Date(hoy.getFullYear(), hoy.getMonth(), 1));
            setSemana(lunesDe(new Date()));
          }}
          className="rounded-lg border border-[var(--color-xk-border)] px-3 py-1.5 text-xs font-bold text-[var(--color-xk-lavender)] hover:bg-[var(--color-xk-surface)]"
        >
          {t('calendar.today')}
        </button>

        <span className="text-xs text-[var(--color-xk-text-muted)]">
          {/* Singular y plural de verdad. `t()` sólo interpola, así que la
              elección se hace aquí: «1 eventos este mes» es el detalle que
              hace que una pantalla parezca sin terminar. */}
          {vista === 'week'
            ? t(delaSemana === 1 ? 'calendar.countThisWeekOne' : 'calendar.countThisWeek', {
                n: delaSemana,
              })
            : t(delMes === 1 ? 'calendar.countThisMonthOne' : 'calendar.countThisMonth', {
                n: delMes,
              })}
        </span>

        <div className="ml-auto flex flex-wrap items-center gap-3">
          {(['live', 'upcoming', 'edit', 'done'] as const).map((f) => (
            <span key={f} className="flex items-center gap-1.5 text-[11px] text-[var(--color-xk-text-muted)]">
              <span aria-hidden className="h-2 w-2 rounded-full" style={{ background: COLOR[f] }} />
              {t(`calendar.phase.${f}`)}
            </span>
          ))}
        </div>
      </header>

      {vista === 'week' ? (
        <SemanaConBarras
          lunes={semana}
          eventos={eventos}
          proyectos={nombreProyecto}
          language={language}
          onAbrir={abrirResumen}
          onCrear={crearEse}
        />
      ) : (
        <>
      <div className="grid grid-cols-7 gap-px overflow-hidden rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-border)]">
        {nombresDeDia(language).map((d) => (
          <div
            key={d}
            className="bg-[var(--color-xk-bg-deep)] py-2 text-center text-[10px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]"
          >
            {d}
          </div>
        ))}

        {celdas.map(({ dia, deOtroMes, eventos: delDia }) => (
          <div
            key={dia.toISOString()}
            className={cn(
              'group relative min-h-[104px] bg-[var(--color-xk-bg)] p-1.5',
              deOtroMes && 'opacity-40',
            )}
          >
            <div className="mb-1 flex items-center gap-1">
              <span
                className={cn(
                  'flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[11px] font-bold',
                  esHoy(dia)
                    ? 'bg-[var(--color-xk-accent)] text-[var(--color-fg-on-accent)]'
                    : 'text-[var(--color-xk-text-muted)]',
                )}
              >
                {dia.getDate()}
              </span>
              {/*
                Crear justo ahí. Aparece al pasar por encima: un «+» fijo en
                cada una de las 35 celdas es ruido en toda la pantalla.

                **Sólo en los días de ESTE mes.** Los de los meses vecinos van
                atenuados y con el mismo número —el 29 de julio y el 29 de
                agosto se ven casi igual—, así que su «+» crea el evento un mes
                antes de donde el usuario cree que está pulsando. Caí en ella
                probando esta misma pantalla: creé una feria el 29 de julio
                mirando agosto, y sólo lo vi al contar los eventos del mes. Los
                días vecinos son contexto, no sitio donde trabajar.
              */}
              {deOtroMes ? null : (
                <button
                  type="button"
                  onClick={() => crearEse(dia)}
                  aria-label={t('calendar.newOnDayShort')}
                  title={t('calendar.newOnDayShort')}
                  className="ml-auto rounded p-0.5 text-[var(--color-xk-text-muted)] opacity-0 transition-opacity hover:bg-[var(--color-xk-surface)] hover:text-[var(--color-fg-primary)] focus-visible:opacity-100 group-hover:opacity-100"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            <div className="flex flex-col gap-1">
              {delDia.slice(0, MAX_POR_DIA).map((e) => {
                const fase = eventPhase(e);
                return (
                  <Link
                    key={e.id}
                    to={`/projects/${e.eventGroupId}/events/${e.id}`}
                    /*
                      Sigue siendo un enlace de verdad aunque el clic normal
                      abra el panel: así ⌘/Ctrl+clic y «abrir en pestaña nueva»
                      siguen llevando al evento. Un `<button>` aquí quitaría
                      las dos cosas sin ganar nada.
                    */
                    onClick={(ev) => {
                      if (ev.metaKey || ev.ctrlKey || ev.shiftKey) return;
                      ev.preventDefault();
                      abrirResumen(e);
                    }}
                    title={`${e.name} · ${nombreProyecto.get(e.eventGroupId) ?? ''}`}
                    className="block rounded px-1 py-0.5 text-[10px] leading-tight hover:bg-[var(--color-xk-surface)]"
                    style={{ borderLeft: `2px solid ${COLOR[fase]}` }}
                  >
                    <span className="block truncate text-[var(--color-fg-primary)]">{e.name}</span>
                    {/*
                      El proyecto, debajo y en gris.
                      
                      Dos eventos llamados «Feria» sólo se distinguen por él, y
                      en la celda del mes no cabe en la misma línea: «Feria · QA
                      F3 · Verano» se corta en «Feria · Q…» y se pierde justo lo
                      que se quería añadir. En segunda línea se lee entero.
                    */}
                    {nombreProyecto.get(e.eventGroupId) ? (
                      <span className="block truncate text-[9px] text-[var(--color-xk-text-muted)]">
                        {nombreProyecto.get(e.eventGroupId)}
                      </span>
                    ) : null}
                  </Link>
                );
              })}
              {/* Con más de tres no se apilan hasta reventar la celda: se dice
                  cuántos faltan y el día completo se ve en la ficha. */}
              {delDia.length > MAX_POR_DIA && (
                <button
                  type="button"
                  // Lleva a la semana de ese día: es donde caben todos sin
                  // recortar, y no a un evento cualquiera de los que sobran.
                  onClick={() => {
                    setSemana(lunesDe(dia));
                    setVista('week');
                  }}
                  className="px-1 text-left text-[10px] text-[var(--color-xk-text-muted)] hover:underline"
                >
                  {t('calendar.more', { n: delDia.length - MAX_POR_DIA })}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

        </>
      )}

      {eventosQ.isLoading ? (
        <p className="mt-3 text-xs text-[var(--color-xk-text-muted)]">{t('common.loading')}</p>
      ) : eventos.length === 0 ? (
        <p className="mt-3 text-xs text-[var(--color-xk-text-muted)]">{t('calendar.empty')}</p>
      ) : null}
    </div>
  );
}

function BotonMes({
  onClick,
  label,
  children,
}: {
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="rounded-lg border border-[var(--color-xk-border)] p-1.5 text-[var(--color-xk-lavender)] hover:bg-[var(--color-xk-surface)]"
    >
      {children}
    </button>
  );
}

/**
 * ¿Este evento ocupa este día?
 *
 * Se compara por DÍA, no por instante: un evento del 10 a las 18:00 al 12 a las
 * 02:00 ocupa el 10, el 11 y el 12. Comparando marcas de tiempo, el 12 se
 * caería en cuanto el día empieza a las 00:00 y el evento «termina» a las 02:00.
 *
 * Sin fecha de fin ocupa sólo su día de inicio; sin fecha de inicio no ocupa
 * ninguno — un evento a medio configurar no puede aparecer en un día inventado.
 */
function ocupa(e: XkEvent, dia: Date): boolean {
  if (!e.startDate) return false;
  const d = +new Date(dia.getFullYear(), dia.getMonth(), dia.getDate());
  const s = new Date(e.startDate);
  const desde = +new Date(s.getFullYear(), s.getMonth(), s.getDate());
  if (!e.endDate) return d === desde;
  const f = new Date(e.endDate);
  const hasta = +new Date(f.getFullYear(), f.getMonth(), f.getDate());
  return d >= desde && d <= hasta;
}

/**
 * Los siete días, empezando en lunes y en el idioma de la herramienta.
 *
 * El 1 de enero de 2024 fue lunes: sirve de semilla para contar siete días sin
 * tener que mantener a mano dos listas de abreviaturas que además habría que
 * traducir.
 */
function nombresDeDia(language: string): string[] {
  return Array.from({ length: 7 }, (_, i) =>
    new Date(2024, 0, 1 + i).toLocaleDateString(language, { weekday: 'short' }),
  );
}

/** `YYYY-MM-DDTHH:mm` local, el protocolo que habla el DatePicker. */
function fechaLocal(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T09:00`;
}

/**
 * La semana, como barras que se extienden por los días que ocupan.
 *
 * No es el mes con menos casillas: es otra forma de mirar. En la rejilla
 * mensual un evento de cinco días son cinco cuadraditos idénticos y hay que
 * seguirlos con el dedo para saber que son el mismo; aquí es **una barra**, y
 * dos barras a la misma altura de la semana son un solape que se ve sin leer
 * nada.
 *
 * Cada evento cae en la primera fila libre. Con eso, el número de filas es
 * exactamente cuántas cosas se pisan a la vez — que es el dato que se busca al
 * abrir la semana antes de un fin de semana cargado.
 */
function SemanaConBarras({
  lunes,
  eventos,
  proyectos,
  language,
  onAbrir,
  onCrear,
}: {
  lunes: Date;
  eventos: XkEvent[];
  /** Id de proyecto → nombre, para rotular la barra con a quién pertenece. */
  proyectos: Map<number, string>;
  language: string;
  onAbrir: (e: XkEvent) => void;
  onCrear: (d: Date) => void;
}) {
  const t = useT();
  const dias = Array.from({ length: 7 }, (_, i) =>
    new Date(lunes.getFullYear(), lunes.getMonth(), lunes.getDate() + i),
  );
  const hoy = new Date();

  // Qué columnas ocupa cada evento dentro de ESTA semana, recortado a ella:
  // una feria que empezó el jueves pasado entra por el lunes.
  const barras = eventos
    .map((e) => {
      const cols = dias.map((d) => ocupa(e, d));
      const desde = cols.indexOf(true);
      if (desde < 0) return null;
      const hasta = cols.lastIndexOf(true);
      return { e, desde, hasta };
    })
    .filter((x): x is { e: XkEvent; desde: number; hasta: number } => x != null)
    .sort((a, b) => a.desde - b.desde || b.hasta - a.hasta);

  // Empaquetado en filas: cada barra baja hasta encontrar una donde no pise a
  // nadie. Sin esto, dos eventos del mismo día se solaparían en pantalla y el
  // solape —lo único que se viene a ver— quedaría escondido.
  const filas: Array<Array<{ e: XkEvent; desde: number; hasta: number }>> = [];
  for (const b of barras) {
    const fila = filas.find((f) => f.every((x) => b.desde > x.hasta || b.hasta < x.desde));
    if (fila) fila.push(b);
    else filas.push([b]);
  }

  return (
    <div className="overflow-hidden rounded-xl border border-[var(--color-xk-border)]">
      <div className="grid grid-cols-7 gap-px bg-[var(--color-xk-border)]">
        {dias.map((d) => {
          const esHoy =
            d.getFullYear() === hoy.getFullYear() &&
            d.getMonth() === hoy.getMonth() &&
            d.getDate() === hoy.getDate();
          return (
            <div key={d.toISOString()} className="group bg-[var(--color-xk-bg-deep)] px-2 py-2">
              <div className="flex items-center gap-1">
                <span className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
                  {d.toLocaleDateString(language, { weekday: 'short' })}
                </span>
                <span
                  className={cn(
                    'flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[11px] font-bold',
                    esHoy
                      ? 'bg-[var(--color-xk-accent)] text-[var(--color-fg-on-accent)]'
                      : 'text-[var(--color-fg-primary)]',
                  )}
                >
                  {d.getDate()}
                </span>
                <button
                  type="button"
                  onClick={() => onCrear(d)}
                  aria-label={t('calendar.newOnDayShort')}
                  title={t('calendar.newOnDayShort')}
                  className="ml-auto rounded p-0.5 text-[var(--color-xk-text-muted)] opacity-0 transition-opacity hover:bg-[var(--color-xk-surface)] hover:text-[var(--color-fg-primary)] focus-visible:opacity-100 group-hover:opacity-100"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="relative bg-[var(--color-xk-bg)] p-2">
        {/* Las siete guías verticales. Sin ellas, una barra que empieza el
            miércoles parece empezar en cualquier sitio. */}
        <div aria-hidden className="pointer-events-none absolute inset-0 grid grid-cols-7">
          {dias.map((d, i) => (
            <div
              key={d.toISOString()}
              className={i === 0 ? '' : 'border-l border-[var(--color-xk-border)]'}
            />
          ))}
        </div>

        {filas.length === 0 ? (
          <p className="py-8 text-center text-xs text-[var(--color-xk-text-muted)]">
            {t('calendar.weekEmpty')}
          </p>
        ) : (
          <div className="relative flex flex-col gap-1.5">
            {filas.map((fila, i) => (
              <div key={i} className="grid grid-cols-7 gap-px">
                {fila.map(({ e, desde, hasta }) => {
                  const fase = eventPhase(e);
                  return (
                    <Link
                      key={e.id}
                      to={`/projects/${e.eventGroupId}/events/${e.id}`}
                      onClick={(ev) => {
                        if (ev.metaKey || ev.ctrlKey || ev.shiftKey) return;
                        ev.preventDefault();
                        onAbrir(e);
                      }}
                      title={e.name}
                      style={{
                        gridColumn: `${desde + 1} / ${hasta + 2}`,
                        background: 'var(--color-xk-surface)',
                        borderLeft: `3px solid ${COLOR[fase]}`,
                      }}
                      className="truncate rounded px-2 py-1.5 text-[11px] text-[var(--color-fg-primary)] hover:brightness-110"
                    >
                      {e.name}
                      {e.startDate ? (
                        <span className="ml-1.5 text-[10px] text-[var(--color-xk-text-muted)]">
                          {new Date(e.startDate).toLocaleTimeString(language, {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      ) : null}
                      {/* Aquí sí cabe en la misma línea: la barra ocupa los días
                          del evento, no la séptima parte de la semana. */}
                      {proyectos.get(e.eventGroupId) ? (
                        <span className="ml-1.5 text-[10px] text-[var(--color-xk-text-muted)]">
                          · {proyectos.get(e.eventGroupId)}
                        </span>
                      ) : null}
                    </Link>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** El lunes de la semana de esa fecha. La semana empieza en lunes, no en domingo. */
function lunesDe(d: Date): Date {
  const desplazamiento = (d.getDay() + 6) % 7;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - desplazamiento);
}
